import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  MagnifyingGlass,
  ShoppingCart,
  Plus,
  Minus,
  Trash,
  Printer,
  CircleNotch,
  X,
  CurrencyDollar,
  Percent,
  Tag
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewSale() {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [completedSale, setCompletedSale] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, customersRes, settingsRes] = await Promise.all([
          axios.get(`${API_URL}/api/products?active_only=true`, { withCredentials: true }),
          axios.get(`${API_URL}/api/customers`, { withCredentials: true }),
          axios.get(`${API_URL}/api/settings`, { withCredentials: true }),
        ]);
        setProducts(productsRes.data);
        setCustomers(customersRes.data);
        setSettings(settingsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    searchRef.current?.focus();
  }, []);

  const filteredProducts = products.filter(p => 
    p.current_stock > 0 && (
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.product_code.toLowerCase().includes(search.toLowerCase()) ||
      p.barcode?.toLowerCase().includes(search.toLowerCase())
    )
  );

  const calcLineTotal = (sellingPrice, qty, taxRate, discountType, discountValue) => {
    const baseAmount = sellingPrice * qty;
    const discountAmt = discountType === 'percent' ? (baseAmount * discountValue / 100) : discountValue;
    const afterDiscount = baseAmount - discountAmt;
    const taxAmount = (afterDiscount * taxRate) / 100;
    return { discount: discountAmt, tax: taxAmount, line_total: afterDiscount + taxAmount };
  };

  const addToCart = (product) => {
    const existing = cart.find(c => c.product_id === product.id);
    if (existing) {
      if (existing.quantity >= product.current_stock) {
        toast.error('Maximum stock reached');
        return;
      }
      updateQuantity(product.id, existing.quantity + 1);
    } else {
      const taxRate = product.tax || 0;
      const { discount, tax, line_total } = calcLineTotal(product.selling_price, 1, taxRate, 'amount', 0);
      setCart([...cart, {
        product_id: product.id, product_name: product.name, product_code: product.product_code,
        quantity: 1, selling_price: product.selling_price,
        discount_type: 'amount', discount_value: 0, discount,
        tax_rate: taxRate, tax,
        line_total, available_stock: product.current_stock
      }]);
    }
    setSearch('');
    searchRef.current?.focus();
  };

  const updateQuantity = (productId, newQty) => {
    if (newQty < 1) { removeFromCart(productId); return; }
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const qty = Math.min(newQty, item.available_stock);
        const { discount, tax, line_total } = calcLineTotal(item.selling_price, qty, item.tax_rate, item.discount_type, item.discount_value);
        return { ...item, quantity: qty, discount, tax, line_total };
      }
      return item;
    }));
  };

  const updateDiscount = (productId, discountType, discountValue) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const val = Math.max(0, discountValue || 0);
        const { discount, tax, line_total } = calcLineTotal(item.selling_price, item.quantity, item.tax_rate, discountType, val);
        return { ...item, discount_type: discountType, discount_value: val, discount, tax, line_total };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => setCart(cart.filter(c => c.product_id !== productId));
  const cartSubtotal = cart.reduce((sum, item) => sum + (item.selling_price * item.quantity), 0);
  const cartDiscount = cart.reduce((sum, item) => sum + item.discount, 0);
  const cartTax = cart.reduce((sum, item) => sum + item.tax, 0);
  const cartTotal = cartSubtotal - cartDiscount + cartTax;

  const handleCompleteSale = async () => {
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    setSaving(true);
    try {
      const payload = {
        customer_name: customerName, phone_number: phoneNumber, payment_method: paymentMethod,
        items: cart.map(item => ({ product_id: item.product_id, quantity: item.quantity, selling_price: item.selling_price, discount: item.discount, tax: item.tax }))
      };
      const res = await axios.post(`${API_URL}/api/sales`, payload, { withCredentials: true });
      toast.success(t('saleComplete'));
      setCompletedSale(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete sale');
    } finally { setSaving(false); }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`<html><head><title>Invoice ${completedSale.invoice_number}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;max-width:400px;margin:0 auto}.header{text-align:center;margin-bottom:20px;border-bottom:2px dashed #000;padding-bottom:10px}.header h1{margin:0;font-size:20px}.header p{margin:5px 0;font-size:12px;color:#666}.info{font-size:12px;margin-bottom:15px}table{width:100%;border-collapse:collapse;font-size:12px}th,td{padding:6px 4px;text-align:left}th{border-bottom:1px solid #000}.total-row td{border-top:2px solid #000;font-weight:bold;font-size:16px}.footer{text-align:center;margin-top:20px;font-size:12px;color:#666;border-top:2px dashed #000;padding-top:10px}</style>
      </head><body>${printContent.innerHTML}</body></html>`);
    printWindow.document.close(); printWindow.focus(); printWindow.print(); printWindow.close();
  };

  const handleNewSale = () => {
    setCompletedSale(null); setCart([]); setCustomerName(''); setPhoneNumber('');
    searchRef.current?.focus();
  };

  if (completedSale) {
    return (
      <div className="max-w-2xl mx-auto animate-slide-up" data-testid="sale-complete">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-[var(--primary)]/10 mx-auto mb-4 flex items-center justify-center rounded-full">
            <ShoppingCart size={40} className="text-[var(--primary)]" />
          </div>
          <h2 className="text-2xl font-bold text-[var(--primary)] mb-2" style={{ fontFamily: 'Outfit' }}>{t('saleComplete')}</h2>
          <p className="text-[var(--dm-text-muted)]">{t('invoice')} {completedSale.invoice_number}</p>
        </div>
        <div className="bg-white text-[#1E293B] p-6 mb-6 rounded-xl border border-[var(--dm-border)] shadow-sm" ref={printRef}>
          <div className="header text-center border-b-2 border-dashed pb-4 mb-4">
            <h1 className="text-xl font-bold">{settings?.store_name || 'DukaanMate'}</h1>
            {settings?.address && <p className="text-sm text-gray-600">{settings.address}</p>}
            {settings?.phone && <p className="text-sm text-gray-600">{settings.phone}</p>}
          </div>
          <div className="info text-sm mb-4">
            <p><strong>Invoice:</strong> {completedSale.invoice_number}</p>
            <p><strong>Date:</strong> {new Date(completedSale.created_at).toLocaleString()}</p>
            {completedSale.customer_name && <p><strong>Customer:</strong> {completedSale.customer_name}</p>}
          </div>
          <table className="w-full text-sm mb-4">
            <thead><tr className="border-b"><th className="text-left py-2">Item</th><th className="text-right py-2">Qty</th><th className="text-right py-2">Price</th><th className="text-right py-2">Disc.</th><th className="text-right py-2">Tax</th><th className="text-right py-2">Total</th></tr></thead>
            <tbody>
              {completedSale.items?.map((item, i) => (
                <tr key={i}>
                  <td className="py-2">{item.product_name}</td>
                  <td className="text-right py-2">{item.quantity}</td>
                  <td className="text-right py-2">${item.selling_price?.toFixed(2)}</td>
                  <td className="text-right py-2">{item.discount > 0 ? `-$${item.discount?.toFixed(2)}` : '-'}</td>
                  <td className="text-right py-2">${item.tax?.toFixed(2)}</td>
                  <td className="text-right py-2">${item.line_total?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr><td colSpan="5" className="text-right py-1 text-gray-600">Subtotal:</td><td className="text-right py-1">${completedSale.subtotal?.toFixed(2)}</td></tr>
              {completedSale.total_discount > 0 && <tr><td colSpan="5" className="text-right py-1 text-[#F97316]">Discount:</td><td className="text-right py-1 text-[#F97316]">-${completedSale.total_discount?.toFixed(2)}</td></tr>}
              {completedSale.total_tax > 0 && <tr><td colSpan="5" className="text-right py-1 text-gray-600">Tax:</td><td className="text-right py-1">${completedSale.total_tax?.toFixed(2)}</td></tr>}
              <tr className="total-row"><td colSpan="5" className="text-right py-3 border-t-2">TOTAL:</td><td className="text-right py-3 border-t-2 text-lg">${completedSale.grand_total?.toFixed(2)}</td></tr>
            </tfoot>
          </table>
          <div className="footer text-center text-sm text-gray-600 border-t-2 border-dashed pt-4">
            <p>{settings?.invoice_footer || t('thankYou')}</p>
          </div>
        </div>
        <div className="flex gap-4">
          <Button onClick={handlePrint} data-testid="print-invoice-btn" className="flex-1 h-14 bg-black text-white font-bold rounded-xl border border-[var(--dm-border)]">
            <Printer size={24} className="mr-2" />{t('printInvoice')}
          </Button>
          <Button onClick={handleNewSale} data-testid="new-sale-again-btn" className="flex-1 h-14 bg-[var(--primary)] text-white font-bold rounded-xl">
            <Plus size={24} className="mr-2" />{t('newSale')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-4" data-testid="new-sale-page">
      {/* Left - Product Search */}
      <div className="flex-1 flex flex-col">
        <div className="relative mb-4">
          <MagnifyingGlass size={24} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--dm-text-muted)]" />
          <Input ref={searchRef} placeholder={t('searchProducts')} value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="product-search-input"
            className="pl-12 h-14 text-lg bg-[var(--card)] border-[var(--dm-border)] text-[var(--text)] rounded-xl focus:border-[var(--primary)] focus:ring-2 focus:ring-[var(--primary)]/20" />
        </div>
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {loading ? (
              <div className="col-span-full flex items-center justify-center py-12"><CircleNotch size={32} className="animate-spin text-[var(--primary)]" /></div>
            ) : filteredProducts.length === 0 ? (
              <div className="col-span-full text-center py-12 text-[var(--dm-text-muted)]">{search ? t('noProductsFound') : t('startTyping')}</div>
            ) : (
              filteredProducts.slice(0, 12).map((product) => (
                <button key={product.id} onClick={() => addToCart(product)} data-testid={`product-${product.id}`}
                  className="p-4 bg-[var(--card)] border border-[var(--dm-border)] text-left rounded-xl hover:border-[var(--primary)] hover:shadow-md transition-all duration-150 active:scale-95">
                  <p className="text-[var(--text)] font-medium truncate">{product.name}</p>
                  <p className="text-[var(--dm-text-muted)] text-xs mb-2">{product.product_code}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--primary)] font-bold font-mono">${product.selling_price?.toFixed(2)}</span>
                    <div className="text-right">
                      {product.tax > 0 && <span className="text-xs text-[var(--dm-text-muted)] block">Tax: {product.tax}%</span>}
                      <span className="text-xs text-[var(--dm-text-muted)]">Stock: {product.current_stock}</span>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Right - Cart */}
      <div className="w-full lg:w-96 flex flex-col bg-[var(--card)] border border-[var(--dm-border)] rounded-xl lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
        <div className="p-4 border-b border-[var(--dm-border)] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingCart size={24} className="text-[var(--primary)]" />
            <h3 className="font-bold text-[var(--text)]" style={{ fontFamily: 'Outfit' }}>{t('cart')} ({cart.length})</h3>
          </div>
          {cart.length > 0 && <button onClick={() => setCart([])} className="text-[var(--dm-text-muted)] hover:text-[#EF4444] text-sm">{t('clear')}</button>}
        </div>
        <div className="p-4 space-y-3">
          {cart.length === 0 ? (
            <p className="text-[var(--dm-text-muted)] text-center py-8">{t('cartEmpty')}</p>
          ) : (
            cart.map((item) => (
              <div key={item.product_id} className="p-3 bg-[var(--bg)] border border-[var(--dm-border)] rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-[var(--text)] text-sm font-medium truncate">{item.product_name}</p>
                    <p className="text-[var(--dm-text-muted)] text-xs">${item.selling_price?.toFixed(2)} each</p>
                    {item.tax_rate > 0 && <p className="text-[var(--dm-text-muted)] text-xs">Tax: {item.tax_rate}% (${item.tax?.toFixed(2)})</p>}
                    {item.discount > 0 && <p className="text-[#F97316] text-xs">Discount: -${item.discount?.toFixed(2)}</p>}
                  </div>
                  <button onClick={() => removeFromCart(item.product_id)} className="text-[var(--dm-text-muted)] hover:text-[#EF4444] p-1"><X size={16} /></button>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                      className="w-8 h-8 bg-[var(--dm-bg-card-hover)] border border-[var(--dm-border)] text-[var(--text)] flex items-center justify-center rounded hover:bg-black transition-colors"><Minus size={16} /></button>
                    <span className="w-10 text-center text-[var(--text)] font-mono">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                      className="w-8 h-8 bg-[var(--dm-bg-card-hover)] border border-[var(--dm-border)] text-[var(--text)] flex items-center justify-center rounded hover:bg-black transition-colors"><Plus size={16} /></button>
                  </div>
                  <span className="text-[var(--primary)] font-bold font-mono">${item.line_total?.toFixed(2)}</span>
                </div>
                {/* Discount row */}
                <div className="flex items-center gap-2 pt-2 border-t border-[var(--dm-border)]/50">
                  <Tag size={14} className="text-[var(--dm-text-muted)] flex-shrink-0" />
                  <div className="flex items-center bg-[var(--dm-bg-card-hover)] border border-[var(--dm-border)] rounded overflow-hidden flex-1">
                    <button
                      onClick={() => updateDiscount(item.product_id, 'percent', item.discount_value)}
                      data-testid={`discount-percent-${item.product_id}`}
                      className={`px-2 py-1 text-xs font-medium transition-colors ${item.discount_type === 'percent' ? 'bg-[var(--primary)] text-black' : 'text-[var(--dm-text-muted)]'}`}
                    >%</button>
                    <button
                      onClick={() => updateDiscount(item.product_id, 'amount', item.discount_value)}
                      data-testid={`discount-amount-${item.product_id}`}
                      className={`px-2 py-1 text-xs font-medium transition-colors ${item.discount_type === 'amount' ? 'bg-[var(--primary)] text-black' : 'text-[var(--dm-text-muted)]'}`}
                    >$</button>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={item.discount_value || ''}
                      onChange={(e) => updateDiscount(item.product_id, item.discount_type, parseFloat(e.target.value))}
                      placeholder="Discount"
                      data-testid={`discount-input-${item.product_id}`}
                      className="flex-1 bg-transparent text-[var(--text)] text-xs px-2 py-1 outline-none w-16 placeholder:text-[var(--dm-text-muted)]/50"
                    />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <div className="p-4 border-t border-[var(--dm-border)] space-y-3">
          <Input placeholder={t('customerName')} value={customerName} onChange={(e) => setCustomerName(e.target.value)}
            data-testid="customer-name-input" className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-lg h-10" />
          <Select value={paymentMethod} onValueChange={setPaymentMethod}>
            <SelectTrigger className="bg-[var(--bg)] border-[var(--dm-border)] text-[var(--text)] rounded-lg h-10" data-testid="payment-method"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[var(--card)] border-[var(--dm-border)]">
              <SelectItem value="cash">{t('cash')}</SelectItem>
              <SelectItem value="card">{t('card')}</SelectItem>
              <SelectItem value="upi">{t('upi')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="p-4 border-t border-[var(--dm-border)] bg-[var(--bg)] rounded-b-xl">
          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-[var(--dm-text-muted)] text-sm">Subtotal</span>
              <span className="text-[var(--text)] font-mono text-sm">${cartSubtotal.toFixed(2)}</span>
            </div>
            {cartDiscount > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-[#F97316] text-sm">Discount</span>
                <span className="text-[#F97316] font-mono text-sm">-${cartDiscount.toFixed(2)}</span>
              </div>
            )}
            {cartTax > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-[var(--dm-text-muted)] text-sm">Tax</span>
                <span className="text-[var(--text)] font-mono text-sm">${cartTax.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-2 border-t border-[var(--dm-border)]">
              <span className="text-[var(--dm-text-muted)] font-bold">{t('total')}</span>
              <span className="text-3xl font-bold font-mono text-[var(--primary)]">${cartTotal.toFixed(2)}</span>
            </div>
          </div>
          <Button onClick={handleCompleteSale} disabled={saving || cart.length === 0} data-testid="pay-btn"
            className="w-full h-16 bg-black text-white font-bold text-xl rounded-xl hover:bg-black/80 disabled:opacity-50 border border-[var(--dm-border)]">
            {saving ? <CircleNotch size={28} className="animate-spin" /> : <><CurrencyDollar size={28} className="mr-2" />{t('payAndPrint')}</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
